import React from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Trash2, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { imgSrc } from "@/lib/img";
import { FreeShippingBar } from "@/components/FreeShippingBar";

export const CartDrawer = () => {
  const { cart, drawerOpen, setDrawerOpen, update, remove } = useCart();

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            data-testid="cart-drawer-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-[70] bg-forestdeep/50 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="fixed top-0 right-0 bottom-0 z-[80] w-full max-w-md bg-cream shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-forest/10">
              <h3 className="font-serif text-2xl text-forest">Your Basket</h3>
              <button data-testid="cart-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close basket" className="p-2 rounded-full hover:bg-forest/5 transition-colors">
                <X size={20} className="text-forest" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {cart.items.length > 0 && <FreeShippingBar />}
              {cart.items.length === 0 && (
                <div data-testid="cart-empty-state" className="text-center py-16">
                  <p className="font-serif text-xl text-forest">Your basket is empty.</p>
                  <p className="text-sm text-mutedgreen mt-2">Nature's finest is waiting for you.</p>
                  <Link to="/collection" data-testid="cart-empty-explore-link" onClick={() => setDrawerOpen(false)} className="inline-flex items-center gap-2 mt-6 bg-forest text-cream text-xs font-semibold uppercase tracking-[0.15em] px-6 py-3 rounded-full hover:bg-forestdeep transition-colors">
                    Explore Collection <ArrowRight size={14} />
                  </Link>
                </div>
              )}
              {cart.items.map((item) => {
                const tid = item.variant_id ? `${item.product.id}-${item.variant_id}` : item.product.id;
                return (
                  <div key={item.key} data-testid={`cart-item-${tid}`} className="flex gap-4">
                    <div className="w-20 h-20 rounded-xl overflow-hidden bg-creamdark shrink-0">
                      <img src={imgSrc(item.product.image)} alt={item.product.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-forest font-medium truncate">{item.product.name}</p>
                      <p className="text-xs text-mutedgreen">{item.variant ? item.variant.label : item.product.unit} · ₹{item.unit_price} each</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border border-forest/15 rounded-full">
                          <button data-testid={`cart-item-${tid}-decrease`} onClick={() => update(item.product.id, item.qty - 1, item.variant_id)} aria-label="Decrease quantity" className="p-1.5 px-2 text-forest hover:text-gold transition-colors"><Minus size={13} /></button>
                          <span data-testid={`cart-item-${tid}-qty`} className="text-sm font-semibold w-6 text-center">{item.qty}</span>
                          <button data-testid={`cart-item-${tid}-increase`} onClick={() => update(item.product.id, item.qty + 1, item.variant_id)} aria-label="Increase quantity" className="p-1.5 px-2 text-forest hover:text-gold transition-colors"><Plus size={13} /></button>
                        </div>
                        <p className="font-semibold text-forest text-sm">₹{item.line_total}</p>
                      </div>
                    </div>
                    <button data-testid={`cart-item-${tid}-remove`} onClick={() => remove(item.product.id, item.variant_id)} aria-label="Remove item" className="self-start p-1.5 text-mutedgreen hover:text-red-700 transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>
                );
              })}
            </div>

            {cart.items.length > 0 && (
              <div className="border-t border-forest/10 px-6 py-5 space-y-4">
                <div className="space-y-1 text-xs text-mutedgreen">
                  <div className="flex justify-between"><span>Subtotal</span><span>₹{cart.subtotal}</span></div>
                  {cart.discount > 0 && <div className="flex justify-between text-gold"><span>Discount</span><span>− ₹{cart.discount}</span></div>}
                  <div className="flex justify-between"><span>Shipping</span><span>{cart.shipping > 0 ? `₹${cart.shipping}` : "Free"}</span></div>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-mutedgreen">Total</span>
                  <span data-testid="cart-drawer-subtotal" className="font-serif text-2xl text-forest">₹{cart.total}</span>
                </div>
                <Link
                  to="/cart"
                  data-testid="cart-drawer-view-cart-button"
                  onClick={() => setDrawerOpen(false)}
                  className="w-full flex items-center justify-center gap-2 bg-forest text-cream text-xs font-semibold uppercase tracking-[0.18em] py-4 rounded-full hover:bg-forestdeep transition-colors"
                >
                  View Basket & Checkout <ArrowRight size={14} />
                </Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
